import { ALLOCATION_STAMP_TYPES, ATTRS, DDI, TAGS, TIME_TYPES } from '../constants';
import { EntitiesManager } from '../entitiesManager';
import { DDIEntities } from '../DDEntities';
import { Entity, ISOFileInformation } from '../types';
import { AllocationStampEntity } from './AllocationStamp';
import { CulturalPracticeEntity } from './CulturalPractice';
import { DeviceEntity } from './Device';
import { DeviceAllocationEntity } from './DeviceAllocation';
import { FarmEntity } from './Farm';
import { GridEntity } from './Grid';
import { PartfieldEntity } from './Partfield';
import { ProcessDataVariableEntity } from './ProcessDataVariable';
import { TimeEntity } from './Time';
import { TimeLogEntity } from './TimeLog';
import { TreatmentZoneEntity } from './TreatmentZone';
import { WorkerEntity } from './Worker';
import { DeviceElementEntity } from './DeviceElement';
import { DataLogTriggerEntity } from './DataLogTrigger';
import { decodeDDI } from '../utils';
import { PGNInfo, PGNs } from '../PGNs';

// timestamp in milliseconds
type TaskPeriod = {
  start: number | null;
  stop: number | null;
};

type DDEEntity = {
  id: number;
  name: string;
  unit: string;
  bitResolution: number;
};

export type TimeLogAttribute = {
  id: string;
  attributeName: string;
  deviceName: string;
  deviceElementName: string;
  ddentity: DDEEntity;
  pgnInfo?: PGNInfo;
};

export class TaskEntity implements Entity {
  public static fromXML(
    xml,
    matchIDs,
    files: ISOFileInformation[],
    entitiesManager: EntitiesManager
  ) {
    const attrs = xml._attributes;
    const isoId = attrs[ATTRS.Task.TaskId];
    const fieldIsoId = attrs[ATTRS.Task.PartfieldIdRef];
    const fieldEntity = entitiesManager.getEntityByIsoId<PartfieldEntity>(fieldIsoId);

    let culturalPractice;
    if (xml[TAGS.OperTechPractice]) {
      const otp = xml[TAGS.OperTechPractice][0];
      const practiceIsoId = otp._attributes[ATTRS.OperTechPractice.CulturalPracticeIdRef];
      culturalPractice = entitiesManager.getEntityByIsoId(practiceIsoId);
    }

    const workers = (xml[TAGS.WorkerAllocation] || []).map((allocation) => {
      const workerIsoId = allocation._attributes[ATTRS.WorkerAllocation.WorkerIdRef];
      return entitiesManager.getEntityByIsoId(workerIsoId);
    });

    const id = matchIDs[isoId] ? matchIDs[isoId].id : null;

    let prescriptionGeoJSON;

    const treatmentZones: TreatmentZoneEntity[] = (xml[TAGS.TreatmentZone] || []).map((tzXml) =>
      TreatmentZoneEntity.fromXML(tzXml, matchIDs, files, entitiesManager)
    );

    const defaultTZCode = parseInt(attrs[ATTRS.Task.DefaultTreatmentZoneCode], 10);
    const defaultTreatmentZone = treatmentZones.find((tz) => tz.code === defaultTZCode);
    const processDataVariable = defaultTreatmentZone
      ? defaultTreatmentZone.processDataVariables[0]
      : null;

    if (xml[TAGS.Grid]) {
      const gridEntity = GridEntity.fromXML(xml[TAGS.Grid][0], matchIDs, files).entity;
      prescriptionGeoJSON = gridEntity.geometry;
    }

    const timeEntities = (xml[TAGS.Time] || []).map(
      (timeXml) => TimeEntity.fromXML(timeXml, matchIDs, files, entitiesManager).entity
    );

    const farmEntity = entitiesManager.getEntityByIsoId<FarmEntity>(attrs[ATTRS.Task.FarmIdRef]);

    let deviceEntities = [];
    if (xml[TAGS.DeviceAllocation]) {
      const deviceAllocationEntities: DeviceAllocationEntity[] = xml[TAGS.DeviceAllocation].map(
        (danXml) => DeviceAllocationEntity.fromXML(danXml, matchIDs, files, entitiesManager).entity
      );
      deviceEntities = Array.from(
        new Set(deviceAllocationEntities.map((deviceAllocation) => deviceAllocation.device))
      );
    }

    const timeLogs = (xml[TAGS.TimeLog] || []).map(
      (timeLogXml) => TimeLogEntity.fromXML(timeLogXml, matchIDs, files, entitiesManager).entity
    );

    const dataLogTriggers = (xml[TAGS.DataLogTrigger] || []).map(
      (dataLogTriggerXml) => DataLogTriggerEntity.fromXML(dataLogTriggerXml).entity
    );

    const entity = new TaskEntity(
      id,
      attrs[ATTRS.Task.TaskStatus],
      fieldEntity,
      culturalPractice,
      timeEntities,
      attrs[ATTRS.Task.TaskDesignator],
      workers,
      prescriptionGeoJSON,
      processDataVariable,
      farmEntity,
      deviceEntities,
      timeLogs,
      dataLogTriggers
    );

    return { entity, isoId };
  }
  public type = TAGS.Task;

  constructor(
    public id: string,
    public status: string,
    public field: PartfieldEntity,
    public operation: CulturalPracticeEntity,
    public times: TimeEntity[],
    public taskDesignator: string,
    public workers?: WorkerEntity[],
    public prescriptionGeoJSON?: any,
    public processDataVariable?: ProcessDataVariableEntity,
    public farm?: FarmEntity,
    public devices: DeviceEntity[] = [],
    public timeLogs?: TimeLogEntity[],
    public dataLogTriggers: DataLogTriggerEntity[] = []
  ) {}

  public toXML(xmlId: string, entitiesManager: EntitiesManager) {
    let defaultTreatmentZone: TreatmentZoneEntity = null;
    let outOfFieldTreatmentZone: TreatmentZoneEntity = null;
    let prescriptionTreatmentZone: TreatmentZoneEntity = null;
    const grids: GridEntity[] = [];

    if (this.processDataVariable) {
      defaultTreatmentZone = new TreatmentZoneEntity(0, [this.processDataVariable]);
      outOfFieldTreatmentZone = new TreatmentZoneEntity(1, [
        this.processDataVariable.cloneWithZeroValue(),
      ]);
    }

    if (this.prescriptionGeoJSON) {
      prescriptionTreatmentZone = new TreatmentZoneEntity(2, [
        this.processDataVariable.cloneWithZeroValue(),
      ]);

      grids.push(new GridEntity(this.prescriptionGeoJSON, prescriptionTreatmentZone.code));
    }

    const treatmentZones = [
      defaultTreatmentZone,
      outOfFieldTreatmentZone,
      prescriptionTreatmentZone,
    ].filter((e) => e);

    const fieldIsoId = entitiesManager.registerEntity(this.field);
    const practiceIsoId = entitiesManager.registerEntity(this.operation);
    const farmIsoId = entitiesManager.registerEntity(this.farm);

    const deviceAllocations = this.devices.map((device) => {
      const plannedTime = (this.times || []).find((time) => time.timeType === TIME_TYPES.Planned);
      const allocationStamp = plannedTime
        ? new AllocationStampEntity(
            ALLOCATION_STAMP_TYPES.Planned,
            plannedTime.start,
            plannedTime.stop,
            plannedTime.duration
          )
        : null;
      return new DeviceAllocationEntity(device.clientNAME, null, device, allocationStamp);
    });

    return {
      _attributes: {
        [ATTRS.Task.TaskId]: xmlId,
        [ATTRS.Task.TaskStatus]: this.status,
        ...(fieldIsoId && { [ATTRS.Task.PartfieldIdRef]: fieldIsoId }),
        [ATTRS.Task.TaskDesignator]: this.taskDesignator,
        ...(farmIsoId && { [ATTRS.Task.FarmIdRef]: farmIsoId }),
        ...(defaultTreatmentZone && {
          [ATTRS.Task.DefaultTreatmentZoneCode]: defaultTreatmentZone.code,
        }),
        ...(defaultTreatmentZone && {
          [ATTRS.Task.PositionLostTreatmentZoneCode]: defaultTreatmentZone.code,
        }),
        ...(outOfFieldTreatmentZone && {
          [ATTRS.Task.OutOfFieldTreatmentZoneCode]: outOfFieldTreatmentZone.code,
        }),
      },

      [TAGS.OperTechPractice]: {
        _attributes: {
          [ATTRS.OperTechPractice.CulturalPracticeIdRef]: practiceIsoId,
        },
      },

      [TAGS.WorkerAllocation]: this.workers.map((worker) => {
        const workerIsoId = entitiesManager.registerEntity(worker);
        return {
          _attributes: {
            [ATTRS.WorkerAllocation.WorkerIdRef]: workerIsoId,
          },
        };
      }),

      [TAGS.TreatmentZone]: treatmentZones.map((tz) => tz.toXML(null, entitiesManager)),
      [TAGS.Grid]: grids.map((grid) => grid.toXML(null, entitiesManager)),
      [TAGS.Time]: (this.times || []).map((time) => time.toXML(null, entitiesManager)),
      [TAGS.DeviceAllocation]: deviceAllocations.map((deviceAllocation) =>
        deviceAllocation.toXML(null, entitiesManager)
      ),
      [TAGS.TimeLog]: (this.timeLogs || []).map((timeLog) => timeLog.toXML()),
      [TAGS.DataLogTrigger]: (this.dataLogTriggers || []).map((dataLogTrigger) =>
        dataLogTrigger.toXML()
      ),
    };
  }

  public periodFromTimes(): TaskPeriod {
    const allTimestamps = []
      .concat(...(this.times || []).map((time) => [time.start, time.stop]))
      .filter((e) => e);

    return {
      start: allTimestamps.length > 0 ? Math.min(...allTimestamps) : null,
      stop: allTimestamps.length > 1 ? Math.max(...allTimestamps) : null,
    };
  }

  // returns seconds
  public workedTimeFromTimes(): number {
    return (this.times || [])
      .filter((time) => time.timeType === TIME_TYPES.Effective)
      .map((time) => time.duration)
      .reduce((total, duration) => total + duration, 0);
  }

  // format of "id" attribute is synchronized with implementation in the GeoServer plugin
  public getTimeLogAttributes(
    filename: string,
    entitiesManager: EntitiesManager
  ): TimeLogAttribute[] {
    const timeLog = this.timeLogs.find((timeLog) => timeLog.filename === filename);
    if (!timeLog) {
      return [];
    }

    return timeLog.time.dataLogValues.map((logValue) => {
      const parts: (string | number)[] = [`DDI${logValue.DDI}`, logValue.deviceElementIdRef];
      if (logValue.dataLogPGN) {
        parts.push(logValue.dataLogPGN, logValue.dataLogPGNStartBit, logValue.dataLogPGNStopBit);
      }
      const id = parts.join('_');

      const ddentity = DDIEntities[logValue.DDI];
      let attributeName = `DDI${logValue.DDI}`;
      let pgnInfo: PGNInfo;
      if (logValue.DDI === decodeDDI(DDI.PGNBasedData)) {
        pgnInfo = PGNs.find(
          (pgnItem) =>
            pgnItem.PGN === Number(logValue.dataLogPGN) &&
            pgnItem.StartBit === Number(logValue.dataLogPGNStartBit) &&
            pgnItem.StopBit === Number(logValue.dataLogPGNStopBit)
        );
        if (pgnInfo) {
          attributeName = `PGN ${pgnInfo.NAME}`;
        } else {
          attributeName = `PGN ${logValue.dataLogPGN}; bits ${logValue.dataLogPGNStartBit}-${logValue.dataLogPGNStopBit}`;
        }
      } else if (ddentity) {
        attributeName = ddentity.name;
      } else {
        attributeName = `DDI${logValue.DDI}`;
      }

      const deviceElement = entitiesManager.getEntityByIsoId<DeviceElementEntity>(
        logValue.deviceElementIdRef
      );
      const deviceElementName =
        (deviceElement && deviceElement.designator) || logValue.deviceElementIdRef;

      // note that we are looking among all the devices, not only devices of the current task
      const device = entitiesManager
        .getEntitiesOfType<DeviceEntity>(TAGS.Device)
        .find((device) => device.hasDeviceElement(deviceElement));
      const deviceName = (device && device.designator) || '';

      return { id, attributeName, deviceName, deviceElementName, ddentity, pgnInfo };
    });
  }
}
